import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ContentLink } from '@/types';
import { generateShortCode } from '@/utils/deeplinkGenerator';

const COLLECTION = 'contentLinks';

// Check code uniqueness across deeplinks AND contentLinks
const isCodeAvailable = async (code: string): Promise<boolean> => {
  const [dlSnap, clSnap] = await Promise.all([
    getDocs(query(collection(db, 'deeplinks'), where('code', '==', code))),
    getDocs(query(collection(db, COLLECTION), where('code', '==', code))),
  ]);
  return dlSnap.empty && clSnap.empty;
};

// Generate a unique code
const generateUniqueCode = async (): Promise<string> => {
  let code = generateShortCode(7);
  let attempts = 0;
  while (!(await isCodeAvailable(code)) && attempts < 10) {
    code = generateShortCode(7);
    attempts++;
  }
  return code;
};

// Create a content link for a platform content
export const createContentLink = async (
  userId: string,
  contentId: string,
  title?: string
): Promise<ContentLink> => {
  const code = await generateUniqueCode();
  const now = serverTimestamp();

  const docRef = await addDoc(collection(db, COLLECTION), {
    userId,
    contentId,
    code,
    title: title || '',
    accessMode: 'public',
    status: 'active',
    downloads: 0,
    views: 0,
    createdAt: now,
    updatedAt: now,
  });

  return {
    id: docRef.id,
    userId,
    contentId,
    code,
    title: title || '',
    accessMode: 'public',
    status: 'active',
    downloads: 0,
    views: 0,
  } as ContentLink;
};

// Get content link for a specific content
export const getContentLinkByContentId = async (
  userId: string,
  contentId: string
): Promise<ContentLink | null> => {
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('contentId', '==', contentId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as ContentLink;
};

// Get all content links for a user
export const getUserContentLinks = async (userId: string): Promise<ContentLink[]> => {
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as ContentLink[];
};

// Toggle content link status
export const toggleContentLinkStatus = async (
  linkId: string,
  status: 'active' | 'disabled'
): Promise<void> => {
  await updateDoc(doc(db, COLLECTION, linkId), {
    status,
    updatedAt: serverTimestamp(),
  });
};
